import type { ReactNode } from 'react';
import { replaceAny } from 'replace-any';

import { PLURAL_PLACEHOLDER_NAME, REGEXP_PLACEHOLDER, REGEXP_WRAPPER } from './constants';
import { lookup } from './lookup';
import { interpolateAll } from './string';
import type { PhraseMap, PlaceholderMap, ReactTokenMap, SafeStrMap } from './types';

const localizeNumericPlaceholders = (placeholders: PlaceholderMap, locale: string) =>
  Object.keys(placeholders).reduce((updatedPlaceholders, currentPlaceholder) => {
    const currentValue = placeholders[currentPlaceholder];

    if (typeof currentValue === 'number') {
      return {
        ...updatedPlaceholders,
        [currentPlaceholder]: Number(currentValue).toLocaleString(locale),
      };
    }

    return {
      ...updatedPlaceholders,
      [currentPlaceholder]: currentValue,
    };
  }, {});

/**
 * Returns the interpolated string for the specified key. If the string is not found, it throws.
 *
 * @param key String key.
 * @param placeholders Placeholders to be interpolated in the string.
 */
type Interpolate = (locale: string, phrases: PhraseMap, key: string, placeholders?: PlaceholderMap) => string;
export const interpolate: Interpolate = (locale, phrases, key, placeholders = {}) => {
  const str = lookup(locale, phrases, key, placeholders[PLURAL_PLACEHOLDER_NAME]);

  const placeholdersWithLocalizedPlurals = localizeNumericPlaceholders(placeholders, locale);

  return interpolateAll(REGEXP_PLACEHOLDER, str, placeholdersWithLocalizedPlurals);
};

/**
 * Returns the interpolated string for the specified key. If the string is not found, it returns the key.
 *
 * @param key String key.
 * @param placeholders Placeholders to be interpolated in the string.
 */
type InterpolateSafe = (locale: string, phrases: PhraseMap, key: string, placeholders?: PlaceholderMap) => string;
export const interpolateSafe: InterpolateSafe = (locale, phrases, key, placeholders) => {
  try {
    return interpolate(locale, phrases, key, placeholders);
  } catch {
    return '';
  }
};

type InterpolateWithReactWrappers = (
  locale: string,
  phrases: PhraseMap,
  key: string,
  placeholders?: ReactTokenMap
) => ReactNode;
export const interpolateWithReactWrappers: InterpolateWithReactWrappers = (locale, phrases, key, placeholders = {}) => {
  const wrappers = [
    {
      regexp: REGEXP_WRAPPER,
      replace: (_key: string, _match: string, wrapper: string, text: string): ReactNode => {
        const trimmedText = text.trim();
        const wrapperFn = placeholders[wrapper];

        if (typeof wrapperFn !== 'function') {
          throw new Error(`Wrapper "${wrapper}" was not provided`);
        }

        return wrapperFn(trimmedText);
      },
    },
  ];

  return replaceAny(wrappers, interpolate(locale, phrases, key, placeholders));
};

type InterpolateWithReactWrappersSafe = (
  locale: string,
  phrases: PhraseMap,
  key: string,
  placeholders?: ReactTokenMap
) => ReactNode;
export const interpolateWithReactWrappersSafe: InterpolateWithReactWrappersSafe = (
  locale,
  phrases,
  key,
  placeholders
) => {
  try {
    return interpolateWithReactWrappers(locale, phrases, key, placeholders);
  } catch {
    return '';
  }
};

/**
 * This is used to return a string interpolator function for all yammer-string feature files. The auto-generated /dist
 * files in yammer-strings include the list of strings which need to be interpolated with interpolateWithReactWrappersSafe.
 * They're passed in here for each file. Other than that, the /dist files are identical.
 * @param safeStrs
 * @param feature
 * @returns
 */
export const getStringInterpolator = (
  safeStrs: SafeStrMap,
  feature: string
): ((locale: string, phrases: PhraseMap) => Record<string, (args: PlaceholderMap) => ReactNode | string>) & {
  readonly feature: string;
} => {
  const StringInterpolator = function (locale: string, phrases: PhraseMap) {
    const placeholderObj = {};

    return new Proxy(placeholderObj, {
      get(_target, prop: string) {
        return function (args: PlaceholderMap) {
          if (safeStrs[prop]) {
            return interpolateWithReactWrappersSafe(locale, phrases, prop, args);
          }

          return interpolateSafe(locale, phrases, prop, args);
        };
      },
    });
  };
  StringInterpolator.feature = feature;

  return StringInterpolator as ((
    locale: string,
    phrases: PhraseMap
  ) => Record<string, (args: PlaceholderMap) => ReactNode | string>) & { readonly feature: string };
};
